const NODE_WIDTH = 180;
const X_GAP = 54;
const COUPLE_GAP = 18;
const Y_GAP = 155;

const LINEAGE_PARENT = ["parent", "adoptive_parent", "guardian", "step_parent"];
const LINEAGE_CHILD = ["child", "adoptive_child", "ward", "step_child"];
const UNION = ["spouse", "partner"];

export function buildFamilyLayout(members, relationships) {
  if (!members.length) {
    return { nodes: [], unions: [], childLinks: [], width: 1000, height: 700 };
  }

  const memberMap = new Map(members.map((m) => [m.id, m]));
  const parentsByChild = new Map();
  const childrenByParent = new Map();
  const partnersOf = new Map();

  const addParent = (parentId, childId) => {
    if (!memberMap.has(parentId) || !memberMap.has(childId)) return;
    parentsByChild.set(childId, [...(parentsByChild.get(childId) ?? []), parentId]);
    childrenByParent.set(parentId, [...(childrenByParent.get(parentId) ?? []), childId]);
  };
  const addPartner = (a, b) => {
    if (!memberMap.has(a) || !memberMap.has(b)) return;
    partnersOf.set(a, new Set([...(partnersOf.get(a) ?? []), b]));
    partnersOf.set(b, new Set([...(partnersOf.get(b) ?? []), a]));
  };

  for (const rel of relationships) {
    const t = rel.relationshipType;
    if (LINEAGE_PARENT.includes(t)) addParent(rel.personAId, rel.personBId);
    else if (LINEAGE_CHILD.includes(t)) addParent(rel.personBId, rel.personAId);
    else if (UNION.includes(t)) addPartner(rel.personAId, rel.personBId);
  }

  // ---- Generation via longest path from roots (parents always above children) ----
  const generation = new Map();
  members.forEach((m) => generation.set(m.id, 0));
  // relax repeatedly
  for (let iter = 0; iter < members.length + 2; iter++) {
    let changed = false;
    for (const [childId, parents] of parentsByChild.entries()) {
      const maxParentGen = Math.max(...parents.map((p) => generation.get(p) ?? 0));
      if ((generation.get(childId) ?? 0) < maxParentGen + 1) {
        generation.set(childId, maxParentGen + 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  // keep partners on the same generation (use the higher of the two)
  for (const [a, set] of partnersOf.entries()) {
    for (const b of set) {
      const g = Math.max(generation.get(a) ?? 0, generation.get(b) ?? 0);
      generation.set(a, g);
      generation.set(b, g);
    }
  }

  // ---- Group members into units (a couple or a single) per generation ----
  const grouped = new Map(); // gen -> array of unit objects {ids:[...]}
  const placedInUnit = new Set();
  const orderedMembers = [...members];

  for (const member of orderedMembers) {
    if (placedInUnit.has(member.id)) continue;
    const g = generation.get(member.id) ?? 0;
    const unit = { ids: [member.id] };
    placedInUnit.add(member.id);
    // attach same-generation partners next to this person
    const partners = [...(partnersOf.get(member.id) ?? [])].filter(
      (p) => !placedInUnit.has(p) && (generation.get(p) ?? 0) === g
    );
    for (const p of partners) {
      unit.ids.push(p);
      placedInUnit.add(p);
    }
    grouped.set(g, [...(grouped.get(g) ?? []), unit]);
  }

  // ---- Order units within each generation by barycenter of their parents ----
  const unitIndex = new Map(); // memberId -> {gen, order}
  const gens = [...grouped.keys()].sort((a, b) => a - b);

  for (const g of gens) {
    let units = grouped.get(g);
    if (g > 0) {
      const bary = (unit) => {
        const parentOrders = [];
        for (const id of unit.ids) {
          for (const pid of parentsByChild.get(id) ?? []) {
            const info = unitIndex.get(pid);
            if (info) parentOrders.push(info.order);
          }
        }
        return parentOrders.length
          ? parentOrders.reduce((a, b) => a + b, 0) / parentOrders.length
          : Number.POSITIVE_INFINITY; // no known parent → push to the end
      };
      const birthKey = (unit) => {
        let best = "9999-99-99";
        for (const id of unit.ids) {
          const bd = memberMap.get(id)?.birthDate;
          if (bd && bd < best) best = bd;
        }
        return best;
      };
      units = units
        .map((u, i) => ({ u, key: bary(u), b: birthKey(u), i }))
        .sort((a, b) => (a.key - b.key) || (a.b < b.b ? -1 : a.b > b.b ? 1 : 0) || (a.i - b.i))
        .map((x) => x.u);
      grouped.set(g, units);
    }
    units.forEach((unit, order) => {
      unit.order = order;
      unit.ids.forEach((id) => unitIndex.set(id, { gen: g, order }));
    });
  }

  // ---- Compute geometry: place each child unit centred under its parents ----
  const MIN_GAP = X_GAP;
  const unitWidth = (u) => u.ids.length * NODE_WIDTH + Math.max(0, u.ids.length - 1) * COUPLE_GAP;
  const memberCenter = new Map();

  for (const g of gens) {
    const units = grouped.get(g);
    let cursor = null; // right edge of the previously placed unit in this row
    for (const unit of units) {
      const half = unitWidth(unit) / 2;
      // desired centre = average centre of this unit's already-placed parents
      let desired = null;
      if (g > 0) {
        const pcs = [];
        for (const id of unit.ids)
          for (const pid of parentsByChild.get(id) ?? [])
            if (memberCenter.has(pid)) pcs.push(memberCenter.get(pid));
        if (pcs.length) desired = pcs.reduce((a, b) => a + b, 0) / pcs.length;
      }
      const minCenter = cursor == null ? half : cursor + MIN_GAP + half;
      const center = desired == null ? minCenter : Math.max(desired, minCenter);
      let left = center - half;
      unit.ids.forEach((id) => {
        memberCenter.set(id, left + NODE_WIDTH / 2);
        left += NODE_WIDTH + COUPLE_GAP;
      });
      cursor = center + half;
    }
  }

  // second pass (bottom-up): centre each parent unit over the block of its children
  for (let gi = gens.length - 1; gi >= 0; gi--) {
    const g = gens[gi];
    const units = grouped.get(g);
    let cursor = null;
    for (const unit of units) {
      const half = unitWidth(unit) / 2;
      let lo = Infinity;
      let hi = -Infinity;
      for (const id of unit.ids)
        for (const cid of childrenByParent.get(id) ?? [])
          if (memberCenter.has(cid)) {
            lo = Math.min(lo, memberCenter.get(cid));
            hi = Math.max(hi, memberCenter.get(cid));
          }
      const curCenter = memberCenter.get(unit.ids[0]) - NODE_WIDTH / 2 + half;
      const desired = lo <= hi ? (lo + hi) / 2 : curCenter;
      const minCenter = cursor == null ? -Infinity : cursor + MIN_GAP + half;
      const center = Math.max(desired, minCenter);
      let left = center - half;
      unit.ids.forEach((id) => {
        memberCenter.set(id, left + NODE_WIDTH / 2);
        left += NODE_WIDTH + COUPLE_GAP;
      });
      cursor = center + half;
    }
  }

  // normalise horizontally + centre the whole tree in the canvas
  let minLeft = Infinity;
  let maxRight = -Infinity;
  for (const c of memberCenter.values()) {
    minLeft = Math.min(minLeft, c - NODE_WIDTH / 2);
    maxRight = Math.max(maxRight, c + NODE_WIDTH / 2);
  }
  const contentW = maxRight - minLeft;
  const width = Math.max(1000, contentW + 240);
  const offset = (width - contentW) / 2 - minLeft;

  const maxGeneration = Math.max(...gens, 0);
  const height = Math.max(700, (maxGeneration + 1) * Y_GAP + 330);

  const nodes = [];
  for (const g of gens) {
    for (const unit of grouped.get(g)) {
      unit.ids.forEach((id) => {
        const c = memberCenter.get(id) + offset;
        nodes.push({
          ...memberMap.get(id),
          x: c - NODE_WIDTH / 2,
          y: 120 + g * Y_GAP,
          generation: g,
        });
      });
    }
  }

  // ---- Connectors: exactly one union line per couple, one link per child ----
  const unionSeen = new Set();
  const unions = [];
  for (const [a, set] of partnersOf.entries()) {
    for (const b of set) {
      if (!memberMap.has(a) || !memberMap.has(b)) continue;
      const key = [a, b].sort().join("|");
      if (unionSeen.has(key)) continue;
      unionSeen.add(key);
      unions.push({ a, b });
    }
  }

  const childLinks = [];
  for (const [childId, parents] of parentsByChild.entries()) {
    if (!memberMap.has(childId)) continue;
    const uniq = [...new Set(parents)].filter((p) => memberMap.has(p));
    if (!uniq.length) continue;
    childLinks.push({ child: childId, parents: uniq });
  }

  return { nodes, unions, childLinks, width, height };
}
